<!-- eslint-disable vue/no-v-html -->
<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from '@/plugins/i18n';
import PlsDialog from '@/components/PlsDialog.vue';
import type { PlsDialogAction } from '@/components/PlsDialog.vue';
import { analytics, BTN_EVTS } from '@/common/logs';
import type { WidgetThresholdRequestedChannel } from '@/library/cf-v1/usePleasedSupport.types';

enum Route {
  LIVE_CHAT_OVERLOADED = 'LIVE_CHAT_OVERLOADED',
  VOICE_CHAT_OVERLOADED = 'VOICE_CHAT_OVERLOADED',
  BOTH_LIVE_VOICE_CHAT_OVERLOADED = 'BOTH_LIVE_VOICE_CHAT_OVERLOADED',
}

const OVERLOADED_CHANNEL_MODAL_EVENTS = {
  back: 'back',
  openRequest: 'openRequest',
  liveChat: 'liveChat',
  call: 'call',
} as const;

type ThresholdLimitModalEvent = (typeof OVERLOADED_CHANNEL_MODAL_EVENTS)[keyof typeof OVERLOADED_CHANNEL_MODAL_EVENTS];

const MODAL_EVENTS_TO_CLICK_TRACK_EVENTS_MAP: Record<ThresholdLimitModalEvent, string> = {
  [OVERLOADED_CHANNEL_MODAL_EVENTS.back]: BTN_EVTS.MODAL_THRESHOLD_LIMIT_BACK,
  [OVERLOADED_CHANNEL_MODAL_EVENTS.openRequest]: BTN_EVTS.MODAL_THRESHOLD_LIMIT_OPEN_REQUEST,
  [OVERLOADED_CHANNEL_MODAL_EVENTS.liveChat]: BTN_EVTS.MODAL_THRESHOLD_LIMIT_LIVE_CHAT,
  [OVERLOADED_CHANNEL_MODAL_EVENTS.call]: BTN_EVTS.MODAL_THRESHOLD_LIMIT_CALL,
};

const props = withDefaults(
  defineProps<{
    open: boolean;
    requestedChannel: WidgetThresholdRequestedChannel;
    hasLiveChatOnlineAgents?: boolean;
    hasCallOnlineAgents?: boolean;
    liveChatWaitTimeDescription?: string;
    callWaitTimeDescription?: string;
  }>(),
  {
    hasLiveChatOnlineAgents: true,
    hasCallOnlineAgents: true,
    liveChatWaitTimeDescription: '',
    callWaitTimeDescription: '',
  }
);

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'openRequest'): void;
  (e: 'liveChat'): void;
  (e: 'call'): void;
}>();

const { t } = useI18n();

const route = computed(() => {
  if (!props.hasLiveChatOnlineAgents && !props.hasCallOnlineAgents) {
    return Route.BOTH_LIVE_VOICE_CHAT_OVERLOADED;
  }
  if (props.requestedChannel === 'call') {
    return Route.VOICE_CHAT_OVERLOADED;
  }
  return Route.LIVE_CHAT_OVERLOADED;
});

const trackAndEmit = (event: ThresholdLimitModalEvent, emitName: 'back' | 'openRequest' | 'liveChat' | 'call') => {
  const modalEvent = MODAL_EVENTS_TO_CLICK_TRACK_EVENTS_MAP[event];
  if (modalEvent) {
    analytics.trackClick({ name: modalEvent });
  }
  emit(emitName);
};

const plsAction = (
  id: string,
  text: string,
  color: PlsDialogAction['color'],
  event: ThresholdLimitModalEvent,
  emitName: 'back' | 'openRequest' | 'liveChat' | 'call'
): PlsDialogAction => ({
  id,
  text,
  color,
  action: () => trackAndEmit(event, emitName),
});

const dialogModel = computed(() => {
  const hasCallOnlineAgents = props.hasCallOnlineAgents;
  const hasLiveChatOnlineAgents = props.hasLiveChatOnlineAgents;

  const openRequestAction = plsAction(
    'open-request',
    t('page.connect.open_request.text') as string,
    'primary',
    OVERLOADED_CHANNEL_MODAL_EVENTS.openRequest,
    'openRequest'
  );

  const backAction = plsAction(
    'back',
    t('page.connect.overloaded.cancel') as string,
    'text',
    OVERLOADED_CHANNEL_MODAL_EVENTS.back,
    'back'
  );

  if (route.value === Route.LIVE_CHAT_OVERLOADED) {
    const messagePlain = (() => {
      const parts = [t('page.connect.overloaded.common.representatives_are_busy') as string];
      if (hasCallOnlineAgents) {
        parts.push(t('page.connect.overloaded.live_chat_modal.open_request_or_call') as string);
      } else {
        parts.push(t('page.connect.overloaded.common.open_request') as string);
      }
      return parts.join(' ');
    })();

    const actions: PlsDialogAction[] = (() => {
      if (hasCallOnlineAgents) {
        return [
          openRequestAction,
          plsAction('call', t('page.connect.call') as string, 'primary', OVERLOADED_CHANNEL_MODAL_EVENTS.call, 'call'),
          backAction,
        ];
      }
      return [openRequestAction, backAction];
    })();

    return {
      title: t('page.connect.overloaded.live_chat_modal.title') as string,
      messagePlain,
      messageHtml: null as string | null,
      actions,
    };
  }

  if (route.value === Route.VOICE_CHAT_OVERLOADED) {
    const messagePlain = (() => {
      const parts = [t('page.connect.overloaded.common.representatives_are_busy') as string];
      if (hasLiveChatOnlineAgents) {
        parts.push(t('page.connect.overloaded.voice_chat_modal.open_request_or_start_live_chat') as string);
      } else {
        parts.push(t('page.connect.overloaded.common.open_request') as string);
      }
      return parts.join(' ');
    })();

    const actions: PlsDialogAction[] = (() => {
      if (hasLiveChatOnlineAgents) {
        return [
          openRequestAction,
          plsAction(
            'live-chat',
            t('page.connect.live_chat') as string,
            'primary',
            OVERLOADED_CHANNEL_MODAL_EVENTS.liveChat,
            'liveChat'
          ),
          backAction,
        ];
      }
      return [openRequestAction, backAction];
    })();

    return {
      title: t('page.connect.overloaded.voice_chat_modal.title') as string,
      messagePlain,
      messageHtml: null as string | null,
      actions,
    };
  }

  const messageHtml = `${t('page.connect.overloaded.both_live_voice_chat_modal.message')}</br></br>${t(
    'page.connect.overloaded.both_live_voice_chat_modal.bottom_message'
  )}`;

  return {
    title: t('page.connect.overloaded.both_live_voice_chat_modal.title') as string,
    messagePlain: null as string | null,
    messageHtml,
    actions: [openRequestAction, backAction],
  };
});
</script>

<template>
  <PlsDialog
    class="widget-threshold-limit-modal"
    :open="open"
    :title="dialogModel.title"
    action-direction="column"
    :actions="dialogModel.actions"
  >
    <div
      v-if="dialogModel.messageHtml"
      class="widget-threshold-limit-modal__body widget-threshold-limit-modal__body--html"
      v-html="dialogModel.messageHtml"
    />
    <p v-else-if="dialogModel.messagePlain" class="widget-threshold-limit-modal__body">
      {{ dialogModel.messagePlain }}
    </p>
  </PlsDialog>
</template>

<style lang="scss" scoped>
.widget-threshold-limit-modal__body {
  margin-top: 0.5rem;
  text-align: center;
  width: 100%;
  font-size: 0.95rem;
  line-height: 1.4;
}

.widget-threshold-limit-modal__body--html {
  :deep(a) {
    color: inherit;
  }
}

:deep(.pls-dialog-actions) {
  flex-direction: column;
}
</style>
